import { Accordion, Button } from '@zoom/components';
import classnames from 'classnames';
import { equals } from 'ramda';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { RealTimeAdherenceFilterSidebarDropdown } from './RealTimeAdherenceFilterSidebarDropdown';
import { ACTIVE_SIDEBAR_FILTERS_COUNTER, APPLY_FILTER_BUTTON_TAG } from './constants';
import {
  CheckboxFilter,
  CheckboxFilterAccordionType,
  CheckboxFilterTile,
  createCheckboxFilterTileSubtitle,
  supervisedTeamsToCheckboxOptionsObj,
} from '../../components/CheckboxFilterTile/CheckboxFilterTile';
import { Sidebar, SidebarBody, SidebarHeader } from '../../components/layout/Sidebar';
import { SupervisedTeamsResult } from '../../graphql/types';

export type RealTimeAdherenceFilterSidebarState = {
  teams: Record<string, CheckboxFilter>;
};

export const defaultRealTimeAdherenceFilterSidebar: RealTimeAdherenceFilterSidebarState = {
  teams: {},
};

type RealTimeAdherenceFilterSidebarProps = {
  closeSidebar: () => void;
  preOpenedItemIds: string[];
  teamsResult: SupervisedTeamsResult;
  setPreOpenedItemIds: React.Dispatch<React.SetStateAction<string[]>>;
  setSidebarFilter: (value: React.SetStateAction<RealTimeAdherenceFilterSidebarState>) => void;
  sidebarFilter: RealTimeAdherenceFilterSidebarState;
};

export const RealTimeAdherenceFilterSidebar: React.FC<RealTimeAdherenceFilterSidebarProps> = ({
  closeSidebar,
  preOpenedItemIds,
  teamsResult,
  setPreOpenedItemIds,
  setSidebarFilter,
  sidebarFilter,
}) => {
  const { t } = useTranslation();

  const defaultSelectedTeamIds = sidebarFilter.teams;

  // Filters states
  const [selectedTeams, setSelectedTeams] = useState<Record<string, CheckboxFilter>>(
    supervisedTeamsToCheckboxOptionsObj(teamsResult, defaultSelectedTeamIds)
  );

  // Creating arrays[] of assigned Ids
  const assignedTeamIds = Object.keys(selectedTeams).filter((curr) => selectedTeams[curr].assigned);

  // Active filters Ids arrays[]
  const activeTeamIds = Object.keys(sidebarFilter.teams);

  const handleApply = () => {
    const checkedQueues: Record<string, CheckboxFilter> = {};

    assignedTeamIds.forEach((assignedQueueId) => {
      checkedQueues[assignedQueueId] = selectedTeams[assignedQueueId];
    });

    setSidebarFilter({
      teams: checkedQueues,
    });

    closeSidebar();
  };

  const handleResetAllFilters = () => {
    setSelectedTeams(supervisedTeamsToCheckboxOptionsObj(teamsResult, defaultSelectedTeamIds, true));

    setPreOpenedItemIds([]);
  };

  const countOfActiveFilters = [!!activeTeamIds.length].filter((active) => active).length;

  const isFilterActive = assignedTeamIds.length > 0;

  const filterValuesPristine = equals(assignedTeamIds, activeTeamIds);

  // Accordion filter options
  const filters: CheckboxFilterAccordionType[] = [
    {
      className: activeTeamIds.length ? 'filter--active' : '',
      dataTestIdPrefix: 'real-time-adherence-filter-teams',
      id: '1',
      items: selectedTeams,
      name: t('team.team', { count: 2 }),
      setItems: setSelectedTeams,
      subtitle: createCheckboxFilterTileSubtitle(t, selectedTeams, assignedTeamIds),
    },
  ];

  return (
    <Sidebar className="sidebar-filter">
      <SidebarHeader onClose={closeSidebar}>
        <Button
          data-test-id={APPLY_FILTER_BUTTON_TAG}
          // eslint-disable-next-line no-restricted-syntax -- invalid rule for components from @zoom/components
          disabled={filterValuesPristine}
          kind="secondary"
          onClick={handleApply}
        >
          {t('form.apply')}
        </Button>
        <p
          className={classnames('sidebar-filter__header-wrapper mr-auto', {
            'sidebar-filter__header-wrapper--inactive': !countOfActiveFilters,
          })}
          data-test-id={ACTIVE_SIDEBAR_FILTERS_COUNTER}
        >
          <Trans i18nKey="filter.activeFilters" values={{ count: countOfActiveFilters }}>
            {/* space needs to be added within strong tag so the tests can run correctly */}
            {/* @ts-expect-error: Value needs to be interpolated, using double bracelets */}
            <strong>{{ countOfActiveFilters }} </strong>Active filters
          </Trans>
        </p>
        <RealTimeAdherenceFilterSidebarDropdown
          isFilterActive={isFilterActive}
          resetAllFilters={handleResetAllFilters}
          t={t}
        />
      </SidebarHeader>
      <SidebarBody>
        <Accordion
          items={filters}
          label={({ name }: CheckboxFilterAccordionType) => name}
          preOpenedItemIds={preOpenedItemIds}
        >
          {(item) => <CheckboxFilterTile filter={item} />}
        </Accordion>
      </SidebarBody>
    </Sidebar>
  );
};
